import { useMemo } from 'react';
import _ from 'lodash';
import { CompassCardComponentProps } from '@scale/compass-ui/types';
import { Text, Select, Box, FormField } from '@scale/scaleui-radix';
import {
  isTaskIdCompatibleColumn,
  isPriorityCompatibleColumn,
} from '@scale/genai-compass-shared/cards/manually_prioritise_tasks';
import type { ManuallyPrioritiseTasksContext } from '@scale/genai-compass-shared/cards/manually_prioritise_tasks';
import useManuallyPrioritiseTasksCard from 'react-scale/corp/views/DataCompassCardsV2/action/ManuallyPrioritiseTasksCard/useManuallyPrioritiseTasksCard';

type SelectOption = {
  value: string;
  label: string;
};

export default function ManuallyPrioritiseTasksCardComponent({
  context,
  updateCardContext,
  updateCardCanSubmit,
  previousCardData,
}: CompassCardComponentProps<ManuallyPrioritiseTasksContext>) {
  const { taskIdColumn, priorityColumn, setTaskIdColumn, setPriorityColumn } =
    useManuallyPrioritiseTasksCard(context, updateCardContext, updateCardCanSubmit);

  const taskIdColumnOptions: SelectOption[] = useMemo(() => {
    const compatibleColumns = previousCardData?.columns?.filter(isTaskIdCompatibleColumn) ?? [];
    return compatibleColumns.map(({ id, header }) => ({ value: id, label: header ?? id }));
  }, [previousCardData]);

  const priorityColumnOptions: SelectOption[] = useMemo(() => {
    const compatibleColumns = previousCardData?.columns?.filter(isPriorityCompatibleColumn) ?? [];
    return compatibleColumns.map(({ id, header }) => ({ value: id, label: header ?? id }));
  }, [previousCardData]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <Box maxWidth="400px">
        <FormField.Root>
          <FormField.Label htmlFor="task-id-column">Task ID Column</FormField.Label>
          <Select.Root value={taskIdColumn} onValueChange={setTaskIdColumn} required>
            <Select.Trigger color={_.isEmpty(taskIdColumn) ? 'red' : undefined} />
            <Select.Content>
              {taskIdColumnOptions.map(option => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          {_.isEmpty(taskIdColumn) && (
            <Text size="2" color="red">
              Task ID column is required
            </Text>
          )}
        </FormField.Root>

        <FormField.Root className="mt-4">
          <FormField.Label htmlFor="priority-column">Priority Column</FormField.Label>
          <Select.Root value={priorityColumn} onValueChange={setPriorityColumn} required>
            <Select.Trigger color={_.isEmpty(priorityColumn) ? 'red' : undefined} />
            <Select.Content>
              {priorityColumnOptions.map(option => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          {_.isEmpty(priorityColumn) && (
            <Text size="2" color="red">
              Priority column is required
            </Text>
          )}
        </FormField.Root>
      </Box>
    </div>
  );
}
